import { z } from "zod";
import {
  AZURE_ACCESS_TARGET_VALUE_JSON_SCHEMA,
  AZURE_PRINCIPAL_VALUE_JSON_SCHEMA,
  AZURE_RESOURCE_VALUE_JSON_SCHEMA,
  AzureAccessTargetValue,
  AzurePrincipalValue,
  AzureResourceValue,
} from "./azure-access.ts";
import { GitLabAccessConfig, GitLabAccessSelection, gitLabAccessTargetValueJsonSchema } from "./gitlab-access.ts";
import type { JsonSchemaObject } from "./json-schema.ts";
import { ORACLE_ACCESS_TARGET_VALUE_JSON_SCHEMA, OracleAccessSelection } from "./oracle-access.ts";

const NonEmptyString = z.string().trim().min(1);
const Timestamp = z.string().datetime({ offset: true });
const pluginIdentifierPattern = /^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/;
const semanticVersionPattern = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/;

export const HelpdeskPluginIdentifier = z.string().trim().min(1).max(100).regex(pluginIdentifierPattern);
export type HelpdeskPluginIdentifier = z.infer<typeof HelpdeskPluginIdentifier>;

export const HelpdeskPluginVersion = z.string().trim().min(1).max(100).regex(semanticVersionPattern);
export type HelpdeskPluginVersion = z.infer<typeof HelpdeskPluginVersion>;

export const HelpdeskExternalResourceSource = z
  .object({
    pluginId: HelpdeskPluginIdentifier,
    pluginVersion: HelpdeskPluginVersion,
    componentId: HelpdeskPluginIdentifier,
    componentVersion: HelpdeskPluginVersion,
    config: z.lazy(() => HelpdeskPluginConfig),
  })
  .strict();
export type HelpdeskExternalResourceSource = z.infer<typeof HelpdeskExternalResourceSource>;

export const HelpdeskExternalResourceSourceIdentity = HelpdeskExternalResourceSource.omit({ config: true }).strict();
export type HelpdeskExternalResourceSourceIdentity = z.infer<typeof HelpdeskExternalResourceSourceIdentity>;

export const HelpdeskExternalResourceSemantic = z
  .object({
    id: HelpdeskPluginIdentifier,
    version: HelpdeskPluginVersion,
  })
  .strict();
export type HelpdeskExternalResourceSemantic = z.infer<typeof HelpdeskExternalResourceSemantic>;

export const HelpdeskExternalResourceBrowser = z.object({ id: NonEmptyString.max(1_000), label: NonEmptyString.max(1_000).optional() }).strict();
export type HelpdeskExternalResourceBrowser = z.infer<typeof HelpdeskExternalResourceBrowser>;

export const HelpdeskExternalResourceTypedValue = z.union([AzureAccessTargetValue, AzurePrincipalValue, AzureResourceValue, OracleAccessSelection, GitLabAccessSelection]);
export type HelpdeskExternalResourceTypedValue = z.infer<typeof HelpdeskExternalResourceTypedValue>;

export const HelpdeskExternalResourceSubmittedValue = z.union([HelpdeskExternalResourceBrowser, HelpdeskExternalResourceTypedValue]);
export type HelpdeskExternalResourceSubmittedValue = z.infer<typeof HelpdeskExternalResourceSubmittedValue>;

/**
 * Typed submission-value JSON Schema per trusted semantic, keyed `<semantic.id>@<major version>`; the argument is the field's pinned source config.
 * A builder returns undefined when the config is unusable, which callers treat like an unknown semantic.
 */
export const HELPDESK_EXTERNAL_RESOURCE_VALUE_JSON_SCHEMAS: Readonly<Record<string, (config: unknown) => JsonSchemaObject | undefined>> = {
  "azure.resource@1": () => AZURE_RESOURCE_VALUE_JSON_SCHEMA,
  "azure.access.target@1": () => AZURE_ACCESS_TARGET_VALUE_JSON_SCHEMA,
  "azure.access.principal@1": () => AZURE_PRINCIPAL_VALUE_JSON_SCHEMA,
  "oracle.access.target@1": () => ORACLE_ACCESS_TARGET_VALUE_JSON_SCHEMA,
  "gitlab.access.target@1": (config) => {
    const parsed = GitLabAccessConfig.safeParse(config);
    return parsed.success ? gitLabAccessTargetValueJsonSchema(parsed.data.accessLevels) : undefined;
  },
};

export const HelpdeskExternalResourceBrowserValue = HelpdeskExternalResourceBrowser;
export type HelpdeskExternalResourceBrowserValue = HelpdeskExternalResourceBrowser;

export const HelpdeskExternalResourceMaterialized = z
  .object({
    kind: z.literal("externalResource"),
    id: NonEmptyString.max(1_000),
    label: NonEmptyString.max(1_000),
    description: z.string().trim().min(1).max(5_000).optional(),
    source: HelpdeskExternalResourceSourceIdentity,
    canonical: z.lazy(() => HelpdeskPluginConfig),
  })
  .strict();
export type HelpdeskExternalResourceMaterialized = z.infer<typeof HelpdeskExternalResourceMaterialized>;

export const HelpdeskExternalResourceMaterializedValue = HelpdeskExternalResourceMaterialized;
export type HelpdeskExternalResourceMaterializedValue = HelpdeskExternalResourceMaterialized;

export type HelpdeskPluginJsonValue = null | boolean | number | string | HelpdeskPluginJsonValue[] | { [key: string]: HelpdeskPluginJsonValue };
export type HelpdeskPluginConfig = { [key: string]: HelpdeskPluginJsonValue };
export type HelpdeskPluginSubmittedValue = { [key: string]: HelpdeskPluginJsonValue };

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== "object") return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const plainJsonRecord = (): z.ZodType<HelpdeskPluginConfig, z.ZodTypeDef, unknown> =>
  z
    .custom<Record<string, unknown>>(isPlainObject)
    .pipe(z.record(z.string().max(200), HelpdeskPluginJsonValue))
    .superRefine((value, context) => {
      if (Object.keys(value).length > 200) context.addIssue({ code: z.ZodIssueCode.too_big, maximum: 200, type: "array", inclusive: true, message: "objects may contain at most 200 properties" });
    });

export const HelpdeskPluginJsonValue: z.ZodType<HelpdeskPluginJsonValue, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.union([z.null(), z.boolean(), z.number().finite(), z.string().max(100_000), z.array(HelpdeskPluginJsonValue).max(1_000), plainJsonRecord()]),
);

export const HelpdeskPluginConfig: z.ZodType<HelpdeskPluginConfig, z.ZodTypeDef, unknown> = plainJsonRecord();
export const HelpdeskPluginSubmittedValue: z.ZodType<HelpdeskPluginSubmittedValue, z.ZodTypeDef, unknown> = plainJsonRecord();

export const HelpdeskPluginCatalogQuery = z
  .object({
    query: z.string().trim().max(500).default(""),
    cursor: z.string().trim().min(1).max(1_000).optional(),
    limit: z.number().int().min(1).max(100).default(25),
    part: z.string().trim().min(1).max(100).optional(),
    selections: z
      .record(z.string().trim().min(1).max(100), NonEmptyString.max(1_000))
      .superRefine((value, context) => {
        if (Object.keys(value).length > 20) context.addIssue({ code: z.ZodIssueCode.too_big, maximum: 20, type: "array", inclusive: true, message: "at most 20 prior selections are supported" });
      })
      .optional(),
  })
  .strict();
export type HelpdeskPluginCatalogQuery = z.infer<typeof HelpdeskPluginCatalogQuery>;

export const HelpdeskPluginCatalogSelection = z.object({ id: NonEmptyString.max(1_000) }).strict();
export type HelpdeskPluginCatalogSelection = z.infer<typeof HelpdeskPluginCatalogSelection>;

export const HelpdeskPluginCatalogItem = z.object({
  id: NonEmptyString.max(1_000),
  label: NonEmptyString.max(1_000),
  description: z.string().trim().min(1).max(5_000).optional(),
  metadata: HelpdeskPluginConfig.optional(),
});
export type HelpdeskPluginCatalogItem = z.infer<typeof HelpdeskPluginCatalogItem>;

export const HelpdeskPluginCatalogPage = z
  .object({
    items: z.array(HelpdeskPluginCatalogItem).max(100),
    cacheExpiresAt: z.string().datetime().optional(),
    cacheTtlMs: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).optional(),
    emptyReason: z.enum(["no_access_roles"]).optional(),
    nextCursor: z.string().trim().min(1).max(1_000).optional(),
  })
  .strict();
export type HelpdeskPluginCatalogPage = z.infer<typeof HelpdeskPluginCatalogPage>;

export const HelpdeskPluginComponentControl = z.enum(["compact-select", "searchable", "composite"]);
export type HelpdeskPluginComponentControl = z.infer<typeof HelpdeskPluginComponentControl>;

export const HelpdeskPluginComponentStarter = z
  .object({
    label: NonEmptyString.max(500),
    helpText: z.string().trim().min(1).max(5_000).optional(),
    required: z.boolean(),
    config: z.lazy(() => HelpdeskPluginConfig),
    semantic: HelpdeskExternalResourceSemantic.optional(),
  })
  .strict();
export type HelpdeskPluginComponentStarter = z.infer<typeof HelpdeskPluginComponentStarter>;

export const HelpdeskPluginConfigDescriptor = z
  .object({
    key: NonEmptyString.max(200),
    label: NonEmptyString.max(500),
    description: z.string().trim().min(1).max(5_000).optional(),
    kind: z.enum(["text", "number", "boolean", "select", "multi-select", "multi-text"]),
    options: z
      .array(z.object({ id: NonEmptyString.max(200), label: NonEmptyString.max(500) }).strict())
      .max(100)
      .optional(),
    defaultValue: HelpdeskPluginJsonValue.optional(),
  })
  .strict()
  .superRefine((descriptor, context) => {
    if ((descriptor.kind === "select" || descriptor.kind === "multi-select") && descriptor.options === undefined) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["options"], message: "selection descriptors must declare options" });
    }
  });
export type HelpdeskPluginConfigDescriptor = z.infer<typeof HelpdeskPluginConfigDescriptor>;

export const HelpdeskPluginPeopleSourceDescriptor = z.object({ id: HelpdeskPluginIdentifier, label: NonEmptyString.max(500) }).strict();
export type HelpdeskPluginPeopleSourceDescriptor = z.infer<typeof HelpdeskPluginPeopleSourceDescriptor>;

export const HelpdeskPluginComponentAuthoringDescriptor = z
  .object({
    componentId: HelpdeskPluginIdentifier,
    componentVersion: HelpdeskPluginVersion,
    type: z.literal("externalResource"),
    name: NonEmptyString.max(200),
    description: z.string().trim().min(1).max(5_000).optional(),
    control: HelpdeskPluginComponentControl,
    catalog: z.boolean(),
    peopleSources: z.array(HelpdeskPluginPeopleSourceDescriptor).max(100).optional(),
    configDescriptors: z.array(HelpdeskPluginConfigDescriptor),
    semantic: HelpdeskExternalResourceSemantic.optional(),
    starter: HelpdeskPluginComponentStarter,
  })
  .strict();
export type HelpdeskPluginComponentAuthoringDescriptor = z.infer<typeof HelpdeskPluginComponentAuthoringDescriptor>;

export const HelpdeskPluginStarterComponentAuthoringDescriptor = HelpdeskPluginComponentStarter.extend({
  componentId: HelpdeskPluginIdentifier,
  componentVersion: HelpdeskPluginVersion,
}).strict();
export type HelpdeskPluginStarterComponentAuthoringDescriptor = z.infer<typeof HelpdeskPluginStarterComponentAuthoringDescriptor>;

export const HelpdeskPluginStarterAuthoringDescriptor = z
  .object({
    starterId: HelpdeskPluginIdentifier,
    name: NonEmptyString.max(200),
    description: z.string().trim().min(1).max(5_000).optional(),
    components: z.array(HelpdeskPluginStarterComponentAuthoringDescriptor).min(1).max(100),
  })
  .strict();
export type HelpdeskPluginStarterAuthoringDescriptor = z.infer<typeof HelpdeskPluginStarterAuthoringDescriptor>;

export const HelpdeskPluginAuthoringDescriptor = z
  .object({
    pluginId: HelpdeskPluginIdentifier,
    pluginVersion: HelpdeskPluginVersion,
    name: NonEmptyString.max(200),
    description: z.string().trim().min(1).max(5_000).optional(),
    available: z.boolean(),
    components: z.array(HelpdeskPluginComponentAuthoringDescriptor).min(1),
    starters: z.array(HelpdeskPluginStarterAuthoringDescriptor),
  })
  .strict();
export type HelpdeskPluginAuthoringDescriptor = z.infer<typeof HelpdeskPluginAuthoringDescriptor>;

export const HelpdeskPluginSubscriptionActor = z
  .object({
    entraId: NonEmptyString.max(500),
    email: z.string().trim().email().max(500),
    name: NonEmptyString.max(500),
  })
  .strict();
export type HelpdeskPluginSubscriptionActor = z.infer<typeof HelpdeskPluginSubscriptionActor>;

export const HelpdeskPluginSubscription = z
  .object({
    departmentId: z.string().uuid(),
    pluginId: HelpdeskPluginIdentifier,
    pluginVersion: HelpdeskPluginVersion,
    subscribedAt: Timestamp,
    subscribedBy: HelpdeskPluginSubscriptionActor,
    unsubscribedAt: Timestamp.nullable(),
    unsubscribedBy: HelpdeskPluginSubscriptionActor.nullable(),
  })
  .strict()
  .superRefine((subscription, context) => {
    if ((subscription.unsubscribedAt === null) !== (subscription.unsubscribedBy === null)) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: "unsubscribe timestamp and actor must be present together", path: ["unsubscribedAt"] });
    }
  });
export type HelpdeskPluginSubscription = z.infer<typeof HelpdeskPluginSubscription>;

export const SubscribeHelpdeskPluginInput = z
  .object({
    pluginId: HelpdeskPluginIdentifier,
    pluginVersion: HelpdeskPluginVersion,
  })
  .strict();
export type SubscribeHelpdeskPluginInput = z.infer<typeof SubscribeHelpdeskPluginInput>;

export const HelpdeskPluginSubscriptionResponse = z.object({ subscription: HelpdeskPluginSubscription }).strict();
export type HelpdeskPluginSubscriptionResponse = z.infer<typeof HelpdeskPluginSubscriptionResponse>;

export const HelpdeskPluginSubscriptionsResponse = z
  .object({
    plugins: z.array(HelpdeskPluginAuthoringDescriptor),
    subscriptions: z.array(HelpdeskPluginSubscription),
  })
  .strict();
export type HelpdeskPluginSubscriptionsResponse = z.infer<typeof HelpdeskPluginSubscriptionsResponse>;
